import React, { useEffect, useState } from 'react';

import styled from 'styled-components';
import {
  parseDate,
  FormatFunction,
  getInputValue,
} from '@datepicker-react/hooks';
import MaskedInput from 'react-text-mask';

import { Col } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { ProfileReservation } from '@assets/icons';
import {
  FORM_DATE_FORMAT,
  getAgeFromFormattedString,
  getCurrentDate,
  getMoment,
  isAfterDateTime,
} from '@utils/dateUtil';

export interface InputProps {
  date: Date | null;
  disabled?: boolean;
  errorMessage?: string;
  setDate(date: Date | null): void;
  dateFormat: FormatFunction | string;
  label: string;
  placeholder?: string;
  error?: boolean;
  onApplyClick?: () => void;
  ageLimit?: number;
}

const InputWrapper = styled.div`
  position: relative;
  width: auto;
  height: auto;
  display: flex;
`;

const Label = styled.label`
  position: absolute;
  z-index: 1;
  top: 10px;
  left: 36px;

  font-size: 12px;
  font-weight: bold;

  color: ${({ theme }) => theme.palette.darkGrey};

  user-select: none;
  pointer-events: none;
`;

const Mask = styled(MaskedInput)<{ error?: boolean }>`
  border-width: 0 0 1px 0;
  border-color: ${({ error }) =>
    error ? 'rgba(226,35,27, 0.2)' : 'rgba(0, 0, 0, 0.2)'};

  box-sizing: border-box;
  width: 100%;
  height: 60px;
  padding: 33px 18px 15px 36px;

  color: ${({ error }) =>
    error ? 'rgba(226,35,27, 0.5)' : 'rgba(0, 0, 0, 0.5)'};

  font-size: 14px;

  outline: none;

  &:disabled {
    cursor: inherit;
  }

  ::placeholder,
  ::-webkit-input-placeholder {
    opacity: 0.5;
    font-size: 16px;
    color: ${({ theme, error }) =>
      error ? theme.palette.tomato : theme.palette.matterHorn};
  }
  :-ms-input-placeholder {
    opacity: 0.5;
    font-size: 16px;
    color: ${({ theme, error }) =>
      error ? theme.palette.tomato : theme.palette.matterHorn};
  }
`;

const StyledCustomIcon = styled(CustomIcon)`
  top: 15px;
  position: absolute;
  margin-right: 15px;
  opacity: 1 !important;
`;

const Error = styled.span`
  height: 19px;
  margin-top: 8px;
  color: ${({ theme }) => theme.palette.tomato};
  font-size: 14px;
`;

const DATE_LENGTH = 10;
const regex = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
const Input = ({
  disabled,
  setDate,
  date,
  dateFormat = 'dd/MM/yyyy',
  label,
  placeholder,
  error,
  onApplyClick,
  ageLimit = 15,
}: InputProps) => {
  const [searchString, setSearchString] = useState(
    getInputValue(date, dateFormat, '')
  );
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setSearchString(getInputValue(date, dateFormat, ''));
  }, [date]);

  useEffect(() => {
    getDateErrorMessage();
  }, [date, onApplyClick]);

  const handleChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchString(target?.value);
    if (target?.value.length === DATE_LENGTH) {
      const parsedDate = parseDate(
        target.value,
        String(dateFormat),
        new Date()
      );
      if (!isNaN(Number(parsedDate))) {
        setDate(parsedDate);
      } else {
        setDate(null);
        setSearchString('');
      }
    }
  };

  const handleBlur = (): void => {
    if (searchString.length !== DATE_LENGTH) {
      setDate(null);
      setSearchString('');
    }
  };

  const getDateErrorMessage = async () => {
    const moment = await getMoment();
    if (!searchString) {
      setErrorMessage('Bu alanı boş bırakamazsınız.');
    } else if (!regex.test(String(searchString))) {
      setErrorMessage('Geçerli bir tarih giriniz.');
    } else if (
      (regex.test(String(searchString)) &&
        getAgeFromFormattedString(String(searchString), FORM_DATE_FORMAT) >
          ageLimit) ||
      (regex.test(String(searchString)) &&
        isAfterDateTime(
          moment(date),
          moment(
            parseDate(
              getCurrentDate().format(FORM_DATE_FORMAT),
              String(`dd/MM/yyyy`),
              new Date()
            )
          )
        ))
    ) {
      setErrorMessage('Girilen çocuk yaşı hatalıdır.');
    } else {
      setErrorMessage('');
    }
  };

  return (
    <>
      <InputWrapper>
        <StyledCustomIcon
          icon={ProfileReservation}
          fontSize={28}
          paletteColor='tomato'
        />

        <Col>
          <Mask
            disabled={disabled}
            error={Boolean(error && errorMessage)}
            guide={false}
            mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
            onBlur={handleBlur}
            onChange={handleChange}
            type='text'
            value={searchString}
            placeholder={placeholder}
          />
          <Label>{label}</Label>
        </Col>
      </InputWrapper>
      {error && errorMessage ? <Error>{errorMessage}</Error> : null}
    </>
  );
};

export default Input;
